import React, { useState, useEffect, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, FileText, ChevronRight, Scale } from 'lucide-react';
import { api } from '../lib/api';

// Convierte menciones a otros documentos legales en enlaces internos
function LinkedContent({ content, docs, currentSlug }) {
  const names = docs.filter((d) => d.slug !== currentSlug).map((d) => d.name).sort((a, b) => b.length - a.length);
  if (!names.length) return <>{content}</>;
  const regex = new RegExp(`(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = content.split(regex);
  return (
    <>
      {parts.map((part, i) => {
        const match = docs.find((d) => d.name.toLowerCase() === part.toLowerCase());
        return match && match.slug !== currentSlug ? (
          <Link key={i} to={`/legal/${match.slug}`} className="text-amber-600 font-bold hover:underline" data-testid={`legal-crosslink-${match.slug}`}>{part}</Link>
        ) : part;
      })}
    </>
  );
}

// Renderiza el árbol de cláusulas con numeración jerárquica (1., 1.1., 1.1.1.)
function ClauseTree({ clauses, docs, currentSlug, prefix = '' }) {
  return (
    <ol className="space-y-3">
      {clauses.map((c, i) => {
        const num = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
        return (
          <li key={num} className="text-sm text-stone-700 leading-relaxed" data-testid={`legal-clause-${num}`}>
            <div className="flex gap-3">
              <span className="font-black text-stone-800 shrink-0">{num}.</span>
              <p className="whitespace-pre-wrap"><LinkedContent content={c.text} docs={docs} currentSlug={currentSlug} /></p>
            </div>
            {c.children?.length > 0 && (
              <div className="ml-6 mt-3">
                {createElement(ClauseTree, { clauses: c.children, docs, currentSlug, prefix: num })}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function LegalPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.getLegalActive().then(setDocs).catch(() => {});
  }, []);

  useEffect(() => {
    setDoc(null); setError(false);
    api.getLegalActiveDoc(slug).then((d) => {
      setDoc(d);
      document.title = `${d.name} | Natur Autoescuela`;
      window.scrollTo(0, 0);
    }).catch(() => setError(true));
    return () => { document.title = 'Natur Autoescuela Store'; };
  }, [slug]);

  const generales = docs.filter((d) => (d.group || 'generales') === 'generales');
  const especificas = docs.filter((d) => d.group === 'especificas');

  const MenuGroup = ({ title, items }) => items.length > 0 && (
    <div className="mb-8">
      <h2 className="text-xl font-black text-stone-800 uppercase leading-tight mb-4">{title}</h2>
      <ul className="space-y-2.5">
        {items.map((d) => (
          <li key={d.slug}>
            <Link
              to={`/legal/${d.slug}`}
              className={`flex items-center gap-2 text-sm transition-colors ${slug === d.slug ? 'text-amber-600 font-bold' : 'text-stone-600 hover:text-amber-600'}`}
              data-testid={`legal-menu-${d.slug}`}
            >
              {slug === d.slug ? <Scale className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-stone-300" />}
              {d.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="legal-page">
      <Link to="/" className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 transition-colors mb-8">
        <ChevronLeft className="w-4 h-4" /> Volver al inicio
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Menú lateral del Área Legal */}
        <aside data-testid="legal-sidebar">
          <MenuGroup title="Condiciones Generales" items={generales} />
          <MenuGroup title="Condiciones Específicas" items={especificas} />
        </aside>

        {/* Contenido del documento */}
        <main>
          {error && (
            <div className="py-24 text-center">
              <p className="text-stone-500 mb-4">Documento legal no disponible.</p>
              <Link to="/" className="text-teal-600 font-bold">Volver al inicio</Link>
            </div>
          )}
          {!error && !doc && <div className="py-24 text-center text-stone-400">Cargando…</div>}
          {doc && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-7 h-7 text-amber-500" />
                <h1 className="text-2xl md:text-3xl font-black text-stone-800" data-testid="legal-title">{doc.name}</h1>
              </div>
              <p className="text-xs text-stone-400 mb-6">Versión {doc.version} · Publicada el {doc.publishedAt ? new Date(doc.publishedAt).toLocaleDateString('es-ES') : '—'} · Área Legal de NATUR COMMODITIES, S.L.</p>
              <div className="bg-white border border-stone-200 rounded-lg p-6 md:p-8">
                {doc.clauses?.length ? (
                  <ClauseTree clauses={doc.clauses} docs={docs} currentSlug={doc.slug} />
                ) : (
                <p className="text-stone-700 leading-relaxed whitespace-pre-wrap text-sm" data-testid="legal-content">
                  <LinkedContent content={doc.content} docs={docs} currentSlug={doc.slug} />
                </p>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
